import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  RefreshCw,
  User,
  MessageSquare,
  Edit2,
  Search,
  X,
  Save,
  ToggleLeft,
  ToggleRight,
  Send,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import {
  agentApiService,
  apiService,
  AiAgentConversation,
  AiAgentConversationThread,
  ChannelType,
} from "../../services/api";
import { EditCustomerModal } from "../../components/EditCustomerModal";
import { Customer } from "../../services/api";
import socketService from "../../services/socket";

interface AgentConversationMessage {
  _id: string;
  role: "customer" | "agent";
  channel?: ChannelType;
  content: string;
  timestamp: string;
}

const channelLabelMap: Record<ChannelType, string> = {
  whatsapp: "WhatsApp",
  facebook_messenger: "Messenger",
  instagram: "Instagram",
};

export const AgentConversationsPage: React.FC = () => {
  const [threads, setThreads] = useState<AiAgentConversationThread[]>([]);
  const [selectedThread, setSelectedThread] =
    useState<AiAgentConversationThread | null>(null);
  const [selectedConversation, setSelectedConversation] =
    useState<AiAgentConversation | null>(null);
  const [messages, setMessages] = useState<AgentConversationMessage[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [channelFilter, setChannelFilter] = useState<"all" | ChannelType>("all");
  const [editingConversation, setEditingConversation] =
    useState<AiAgentConversation | null>(null);
  const [instructionDraft, setInstructionDraft] = useState("");
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    const socket = socketService.connect();

    const refreshSelectedConversation = () => {
      if (!selectedThread?.chatId) return;
      setTimeout(() => {
        fetchConversation(selectedThread.chatId);
      }, 250);
    };

    const handleAgentMessage = (payload: { chatId: string; channel?: ChannelType }) => {
      fetchThreads();
      if (selectedThread?.chatId === payload.chatId) {
        refreshSelectedConversation();
      }
    };

    const handleGeneralMessage = () => {
      fetchThreads();
      refreshSelectedConversation();
    };

    socket.on("agent_message", handleAgentMessage);
    socket.on("agent_thread_update", handleAgentMessage);
    socket.on("channel_message", handleGeneralMessage);
    socket.on("new_message", handleGeneralMessage);
    socket.on("customer_update", handleGeneralMessage);

    return () => {
      socket.off("agent_message", handleAgentMessage);
      socket.off("agent_thread_update", handleAgentMessage);
      socket.off("channel_message", handleGeneralMessage);
      socket.off("new_message", handleGeneralMessage);
      socket.off("customer_update", handleGeneralMessage);
    };
  }, [selectedThread]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const fetchThreads = async () => {
    setLoadingThreads(true);
    try {
      const data = await agentApiService.getAgentConversationThreads();
      setThreads(data);
      if (
        data.length > 0 &&
        (!selectedThread ||
          !data.find((t) => t.chatId === selectedThread.chatId))
      ) {
        setSelectedThread(data[0]);
        await fetchConversation(data[0].chatId);
      }
    } catch (error) {
      console.error("Error fetching agent threads:", error);
    } finally {
      setLoadingThreads(false);
    }
  };

  const fetchConversation = async (chatId: string) => {
    setLoadingMessages(true);
    try {
      const response = await agentApiService.getAgentConversation(chatId);
      setSelectedConversation(response.conversation);
      setMessages(response.messages);
      setInstructionDraft(response.conversation.customInstruction || "");
    } catch (error) {
      console.error("Error fetching agent conversation:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleRefresh = async () => {
    await fetchThreads();
    if (selectedThread) {
      await fetchConversation(selectedThread.chatId);
    }
  };

  const filteredThreads = useMemo(() => {
    const channelScoped =
      channelFilter === "all"
        ? threads
        : threads.filter((thread) => (thread.channel || "whatsapp") === channelFilter);

    if (!searchQuery.trim()) return channelScoped;
    const query = searchQuery.toLowerCase().trim();
    return channelScoped.filter((thread) => {
      const chatMatch = thread.chatId.toLowerCase().includes(query);
      const nameMatch =
        thread.customer?.name?.toLowerCase().includes(query) || false;
      const phoneMatch =
        thread.customer?.phoneNumber?.toLowerCase().includes(query) || false;
      const waMatch =
        thread.customer?.whatsappId?.toLowerCase().includes(query) || false;
      const externalMatch =
        thread.customer?.externalContactId?.toLowerCase().includes(query) || false;
      const displayMatch =
        thread.customer?.displayName?.toLowerCase().includes(query) || false;
      return chatMatch || nameMatch || phoneMatch || waMatch || externalMatch || displayMatch;
    });
  }, [threads, searchQuery, channelFilter]);

  const handleToggleAI = async (enabled: boolean) => {
    if (!selectedConversation) return;
    try {
      const updated = await agentApiService.toggleConversation(
        selectedConversation.chatId,
        enabled,
      );
      setSelectedConversation(updated);
      setThreads((prev) =>
        prev.map((t) =>
          t.chatId === updated.chatId
            ? { ...t, aiEnabled: updated.aiEnabled }
            : t,
        ),
      );
    } catch (error) {
      console.error("Failed to toggle AI:", error);
    }
  };

  const handleSaveInstruction = async () => {
    if (!selectedConversation) return null;
    try {
      const updated = await agentApiService.updateConversationInstruction(
        selectedConversation.chatId,
        instructionDraft,
      );
      setSelectedConversation(updated);
      setThreads((prev) =>
        prev.map((t) =>
          t.chatId === updated.chatId
            ? { ...t, customInstruction: updated.customInstruction }
            : t,
        ),
      );
      return updated;
    } catch (error) {
      console.error("Failed to save instruction:", error);
      return null;
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversation || !composerText.trim()) return;
    setSendError("");
    const outgoingText = composerText.trim();
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: AgentConversationMessage = {
      _id: tempId,
      role: "agent",
      channel: (selectedConversation.channel || "whatsapp") as ChannelType,
      content: outgoingText,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setThreads((prev) =>
      prev.map((thread) =>
        thread.chatId === selectedConversation.chatId
          ? {
              ...thread,
              lastMessagePreview: outgoingText,
              lastSender: "agent",
              lastUpdated: optimisticMessage.timestamp,
              messageCount: (thread.messageCount || 0) + 1,
            }
          : thread,
      ),
    );
    setComposerText("");
    setSending(true);
    try {
      await agentApiService.sendAgentMessage(
        selectedConversation.chatId,
        outgoingText,
      );
      await fetchConversation(selectedConversation.chatId);
      await fetchThreads();
    } catch (error) {
      console.error("Failed to send agent message:", error);
      setMessages((prev) => prev.filter((msg) => msg._id !== tempId));
      setComposerText(outgoingText);
      const backendError =
        (error as any)?.response?.data?.error ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        "Failed to send message";
      setSendError(String(backendError));
      await fetchThreads();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <h2>AI Conversations</h2>
          <button
            className="refresh-button"
            onClick={handleRefresh}
            disabled={loadingThreads}>
            <RefreshCw size={16} className={loadingThreads ? "spin" : ""} />
          </button>
        </div>

        <div
          className="search-container"
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}>
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}>
            <Search
              size={18}
              style={{
                position: "absolute",
                left: "12px",
                color: "#888",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder="Search by name, phone, or chat..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 36px 10px 40px",
                background: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "10px",
                color: "#000000ff",
                fontSize: "14px",
                outline: "none",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "rgba(99, 102, 241, 0.5)";
                e.target.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "rgba(255, 255, 255, 0.15)";
                e.target.style.boxShadow = "none";
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: "8px",
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "none",
                  borderRadius: "50%",
                  width: "22px",
                  height: "22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#888",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.color = "#888";
                }}>
                <X size={14} />
              </button>
            )}
          </div>
          {searchQuery && (
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#888" }}>
              {filteredThreads.length} result
              {filteredThreads.length !== 1 ? "s" : ""} found
            </div>
          )}
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {(["all", "whatsapp", "facebook_messenger", "instagram"] as const).map((channel) => (
              <button
                key={channel}
                className={`pill ${channelFilter === channel ? "highlight" : "muted"}`}
                onClick={() => setChannelFilter(channel)}
              >
                {channel === "all" ? "All" : channelLabelMap[channel]}
              </button>
            ))}
          </div>
        </div>

        <div className="conversation-list">
          {loadingThreads ? (
            <div
              style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              Loading conversations...
            </div>
          ) : filteredThreads.length === 0 ? (
            <div
              style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              {searchQuery
                ? "No matching conversations found"
                : "No conversations yet"}
            </div>
          ) : (
            filteredThreads.map((thread) => (
              <div
                key={thread.chatId}
                className={`conversation-item ${selectedThread?.chatId === thread.chatId ? "active" : ""}`}
                onClick={async () => {
                  setSelectedThread(thread);
                  await fetchConversation(thread.chatId);
                }}>
                <div className="conversation-avatar">
                  <User size={24} />
                </div>
                <div className="conversation-content">
                  <div className="conversation-header">
                    <span className="conversation-name">
                      {thread.customer?.name ||
                        thread.customer?.displayName ||
                        thread.customer?.whatsappName ||
                        thread.chatId}
                    </span>
                    <span
                      className="pill muted"
                      style={{ fontSize: "10px", marginLeft: "8px" }}
                    >
                      {channelLabelMap[(thread.channel || "whatsapp") as ChannelType]}
                    </span>
                    <span className="conversation-time">
                      {formatDistanceToNow(new Date(thread.lastUpdated), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <p className="conversation-preview">
                    <span
                      style={{
                        fontWeight:
                          thread.lastSender === "customer" ? "bold" : "normal",
                      }}>
                      {thread.lastSender === "customer" ? "👤 " : "🤖 "}
                    </span>
                    {thread.lastMessagePreview}
                  </p>
                </div>
                <span
                  className="message-count"
                  style={{ fontSize: "12px", color: "#666" }}>
                  {thread.messageCount}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="chat-main">
        {selectedConversation ? (
          <>
            <div className="chat-header">
              <div className="chat-title">
                <h3>
                  {selectedConversation.customerId?.name ||
                    selectedConversation.customerId?.displayName ||
                    selectedConversation.customerId?.whatsappName ||
                    selectedConversation.chatId}
                </h3>
                <span className="online-status">
                  {selectedConversation.aiEnabled ? "AI Enabled" : "AI Stopped"}
                </span>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-secondary)",
                  }}>
                  <span>Chat ID: {selectedConversation.chatId}</span>
                  <span style={{ marginLeft: "0.75rem" }}>
                    Channel: {channelLabelMap[(selectedConversation.channel || "whatsapp") as ChannelType]}
                  </span>
                  {selectedConversation.customerId?.phoneNumber && (
                    <span style={{ marginLeft: "0.75rem" }}>
                      Phone: {selectedConversation.customerId.phoneNumber}
                    </span>
                  )}
                  {selectedConversation.customerId?.whatsappName && (
                    <span style={{ marginLeft: "0.75rem" }}>
                      WhatsApp: {selectedConversation.customerId.whatsappName}
                    </span>
                  )}
                  {selectedConversation.customerId?.externalContactId &&
                    selectedConversation.channel !== "whatsapp" && (
                      <span style={{ marginLeft: "0.75rem" }}>
                        Contact: {selectedConversation.customerId.externalContactId}
                      </span>
                    )}
                  <span style={{ marginLeft: "0.75rem" }}>
                    Last message:{" "}
                    {new Date(
                      selectedConversation.lastMessageAt,
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="heading-actions">
                <button
                  className="ghost-button"
                  onClick={() =>
                    handleToggleAI(!selectedConversation.aiEnabled)
                  }>
                  {selectedConversation.aiEnabled ? (
                    <ToggleRight size={16} />
                  ) : (
                    <ToggleLeft size={16} />
                  )}
                  {selectedConversation.aiEnabled ? "Stop AI" : "Enable AI"}
                </button>
                <button
                  className="ghost-button"
                  onClick={() => setEditingConversation(selectedConversation)}
                  title="Edit Conversation">
                  <Edit2 size={16} /> Edit
                </button>
                {selectedConversation.customerId && (
                  <button
                    className="ghost-button"
                    onClick={() =>
                      setEditingCustomer({
                        _id: selectedConversation.customerId!._id,
                        whatsappId:
                          selectedConversation.customerId!.whatsappId ||
                          selectedConversation.chatId,
                        whatsappName:
                          selectedConversation.customerId!.whatsappName,
                        name: selectedConversation.customerId!.name,
                        phoneNumber:
                          selectedConversation.customerId!.phoneNumber,
                        firstContactDate: "",
                        lastContactDate: "",
                        metadata: { totalMessages: 0 },
                        createdAt: "",
                        updatedAt: "",
                      })
                    }>
                    <Edit2 size={16} /> Edit Lead
                  </button>
                )}
              </div>
            </div>

            <div className="messages-container">
              {loadingMessages ? (
                <div className="no-messages">Loading messages...</div>
              ) : messages.length > 0 ? (
                messages.map((message, index) => (
                  <div
                    key={message._id || index}
                    className={`message-bubble ${message.role === "agent" ? "sent" : "received"}`}>
                    <div
                      className="message-sender"
                      style={{
                        fontSize: "11px",
                        color: message.role === "agent" ? "#0084ff" : "#666",
                        marginBottom: "4px",
                        fontWeight: "bold",
                      }}>
                      {message.role === "agent" ? " " : "👤 Customer"}
                    </div>
                    <div className="message-text">{message.content}</div>
                    <div className="message-time">
                      {format(new Date(message.timestamp), "h:mm a")}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-messages">
                  <MessageSquare size={48} className="text-muted" />
                  <p>No messages in this conversation</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input-container">
              <div className="message-input-wrapper">
                <input
                  className="message-input"
                  placeholder="Type a message to this customer..."
                  value={composerText}
                  onChange={(e) => setComposerText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <button
                  className="send-button"
                  onClick={handleSendMessage}
                  disabled={sending || !composerText.trim()}
                  title="Send message">
                  <Send size={18} />
                </button>
              </div>
              {sendError ? (
                <div
                  style={{
                    marginTop: "8px",
                    color: "#d32f2f",
                    fontSize: "12px",
                    lineHeight: 1.35,
                  }}
                >
                  {sendError}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="no-chat-selected">
            <MessageSquare size={64} className="text-muted" />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the sidebar to start messaging</p>
          </div>
        )}
      </div>

      <EditAgentConversationModal
        conversation={editingConversation}
        isOpen={!!editingConversation}
        instruction={instructionDraft}
        onClose={() => setEditingConversation(null)}
        onInstructionChange={setInstructionDraft}
        onSave={async () => {
          if (editingConversation) {
            await handleSaveInstruction();
            await fetchConversation(editingConversation.chatId);
            await fetchThreads();
          }
        }}
        onToggle={async (enabled) => {
          if (editingConversation) {
            await handleToggleAI(enabled);
          }
        }}
      />

      <EditCustomerModal
        customer={editingCustomer}
        isOpen={!!editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSave={async () => {
          if (selectedConversation) {
            await fetchConversation(selectedConversation.chatId);
            await fetchThreads();
          }
        }}
      />
    </div>
  );
};

interface EditAgentConversationModalProps {
  conversation: AiAgentConversation | null;
  isOpen: boolean;
  instruction: string;
  onClose: () => void;
  onInstructionChange: (value: string) => void;
  onSave: () => void;
  onToggle: (enabled: boolean) => Promise<void>;
}

const EditAgentConversationModal: React.FC<EditAgentConversationModalProps> = ({
  conversation,
  isOpen,
  instruction,
  onClose,
  onInstructionChange,
  onSave,
  onToggle,
}) => {
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);

  useEffect(() => {
    if (conversation?.customerId) {
      setEditName(conversation.customerId.name || "");
      setEditPhone(conversation.customerId.phoneNumber || "");
    } else {
      setEditName("");
      setEditPhone("");
    }
    if (conversation) {
      setAiEnabled(conversation.aiEnabled);
    }
  }, [conversation]);

  if (!isOpen || !conversation) return null;

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      if (conversation.customerId?._id) {
        const cleanPhone = editPhone.replace(/\+/g, "");
        await apiService.updateCustomer(conversation.customerId._id, {
          name: editName,
          phoneNumber: cleanPhone,
        });
      }
      await onSave();
      onClose();
    } catch (error) {
      console.error("Failed to update conversation/customer:", error);
      alert("Failed to update. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Conversation</h3>
          <button className="ghost-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          {/* <div className="readonly-info-card form-group">
            <label>Chat ID</label>
            <div className="value">{conversation.chatId}</div>
          </div> */}

          {/* <div className="readonly-info-card form-group">
            <label>WhatsApp ID</label>
            <div className="value">
              {conversation.customerId?.whatsappId || conversation.chatId}
            </div>
          </div> */}

          {/* <div className="readonly-info-card form-group">
            <label>WhatsApp Name (from contact)</label>
            <div
              className={`value ${conversation.customerId?.whatsappName ? "" : "muted"}`}>
              {conversation.customerId?.whatsappName || "Not available"}
            </div>
          </div> */}

          {/* <div className="form-section-divider">Editable Fields</div> */}

          {/* <div className="form-group">
            <label>Lead Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter lead name"
              className="input-field"
            />
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="text"
              value={editPhone}
              onChange={(e) =>
                setEditPhone(e.target.value.replace(/[^0-9]/g, ""))
              }
              placeholder="e.g. 201234567890 (digits only)"
              className="input-field"
            />
            <p className="form-hint">
              Enter digits only, without country code prefix (+)
            </p>
          </div> */}

          <div className="readonly-info-card form-group">
            <label>AI Status</label>
            <button
              className="ghost-button"
              onClick={async () => {
                const next = !aiEnabled;
                setAiEnabled(next);
                await onToggle(next);
              }}
            >
              {aiEnabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              {aiEnabled ? "Enabled" : "Stopped"}
            </button>
          </div>

          <div className="form-group">
            <label>Instruction</label>
            <textarea
              className="input-field"
              rows={4}
              value={instruction}
              onChange={(e) => onInstructionChange(e.target.value)}
              placeholder="Optional instruction for this chat"
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="ghost-button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="primary-button"
            onClick={handleSaveAll}
            disabled={saving}>
            {saving ? (
              <>
                <span
                  className="spinner small"
                  style={{ width: 16, height: 16 }}></span>
                Saving...
              </>
            ) : (
              <>
                Save Changes
                <Save size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
